<template>
  <vue-final-modal
    :model-value="modelValue"
    :class="[
      'custom-modal-vfm',
      { 'custom-modal-vfm--bottom': position === 'bottom', 'custom-modal-vfm--seamless': isSeamless },
    ]"
    :lock-scroll="lockScroll"
    :hide-overlay="isSeamless"
    :horizontal-align="horizontalAlign"
    :click-to-close="!persistent"
    :esc-to-close="!persistent"
    :content-class="[
      isTouch ? 'touch-device' : '',
      $attrs['content-class'],
      isTab && isMaximized ? 'custom-modal--maximized' : '',
    ]"
    :content-transition="resolvedContentTransition"
    :content-style="contentStyle"
    v-bind="$attrs"
    @update:model-value="closeModal"
  >
    <component
      :is="modalTemplate"
      v-bind="props.props"
      :style="{ width: modalWidth }"
      :class="{ 'custom-modal--big-size': isBigSize }"
      @on-ok="onOk"
      @close-modal="closeModal"
      @back="backToRegistrationModal"
    >
      <template #closeButton="closeButtonProps">
        <div
          v-if="!$slots.header && !title"
          class="custom-modal__close-btn"
          :data-name="closeButtonProps?.dataName"
          @click="closeModal"
        >
          <icon name="close" />
        </div>
      </template>
      <template #header>
        <div v-if="$slots.header || title" class="custom-modal__header-wrapper text-adaptive-h2">
          <ol-safe-html v-if="title" class="custom-modal__header" :html="title" />
          <slot name="header" />
          <div class="custom-modal__close-btn" @click="closeModal">
            <icon name="close" />
          </div>
        </div>
      </template>
      <template #body>
        <ol-safe-html
          v-if="bodyHtml"
          class="custom-modal__body"
          :class="{ 'custom-modal__body--ios': $helpers.platform.is.ios }"
          :html="bodyHtml"
        />
        <slot v-else-if="customBody"></slot>
        <div v-else class="custom-modal__body" :class="{ 'custom-modal__body--ios': $helpers.platform.is.ios }">
          <slot></slot>
          {{ body }}
        </div>
        <div v-if="!footer && actions" class="custom-modal__actions">
          <ol-button v-if="hasClose" :label="$t('close')" variant="secondary" @click="closeModal" />
          <ol-button
            v-for="(button, index) in actions"
            :key="index"
            :class="button.class"
            v-bind="button.props"
            @click="button.action"
          />
        </div>
      </template>
      <template v-if="hasFooter" #footer>
        <ol-button v-if="hasClose" :label="$t('close')" variant="secondary" @click="closeModal" />
        <ol-button
          v-for="(button, index) in actions"
          :key="index"
          v-bind="button"
          :loading="loadingObject[index]"
          :disabled="button.disabled"
          @click="() => modalAction(index, button)"
        />
        <slot name="footer"></slot>
      </template>
    </component>
  </vue-final-modal>
</template>

<script setup>
import { computed, defineAsyncComponent, inject, markRaw, ref, useSlots, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { OlButton, OlSafeHtml } from '@gamesite/game-core'
import { useHelpers } from 'common/composition/useHelpers.js'
import { VueFinalModal } from 'vue-final-modal'
import Icon from 'components/ui/Icon.vue'
import { useAuth } from 'hooks/useAuth.js'
import { useModalsStore } from 'stores/modals/index.js'
import { useSiteInfoStore } from 'src/stores/site-info'
import { useModalContentTransition } from 'src/common/functions'
import { postMessageFromWebviewToApp } from 'src/common/webview'

const props = defineProps({
  modelValue: Boolean,
  persistent: {
    type: Boolean,
    default: false,
  },
  hasClose: Boolean,
  isMaximized: {
    type: Boolean,
    default: true,
  },
  isBigSize: {
    type: [Boolean, null],
    default: false,
  },
  title: {
    type: String,
    default: null,
  },
  body: {
    type: String,
    default: null,
  },
  bodyHtml: {
    type: String,
    default: null,
  },
  footer: {
    type: Boolean,
    default: true,
  },
  customBody: {
    type: Boolean,
    default: false,
  },
  actions: {
    type: Array,
    default: null,
  },
  componentName: {
    type: [Function, Object],
    default: null,
  },
  props: {
    type: Object,
    default: () => ({}),
  },
  resolve: {
    type: Function,
    default: null,
  },
  reject: {
    type: Function,
    default: null,
  },
  isSeamless: {
    type: Boolean,
    default: false,
  },
  position: {
    type: String,
    default: 'standard',
  },
  horizontalAlign: {
    type: String,
    default: 'center',
  },
  zIndex: {
    type: Number,
    default: null,
  },
  lockScroll: {
    type: Boolean,
    default: true,
  },
  contentTransition: {
    type: [String, Object],
    default: '',
  },
})

const emit = defineEmits(['update:model-value'])
const slots = useSlots()
const route = useRoute()
const router = useRouter()

const { register } = useAuth()

const $helpers = useHelpers()

const mediaQueries = inject('mediaQueries')

const loadingObject = ref({})

const isTab = computed(() => mediaQueries.mobile)
const isTouch = computed(() => $helpers.platform.has.touch)
const resolvedContentTransition = useModalContentTransition(
  computed(() => props.contentTransition),
  mediaQueries,
)

const modalWidth = computed(() => {
  if (isTab.value) {
    return '100%'
  }
  if (typeof props.isBigSize === 'boolean') {
    return props.isBigSize ? '934px' : '450px'
  }
  return null
})

const contentStyle = computed(() => {
  const style = { width: modalWidth.value }
  if (props.zIndex) {
    style.zIndex = String(props.zIndex)
  }
  return style
})

const modalTemplate = computed(
  () => props.componentName ?? markRaw(defineAsyncComponent(() => import('./ModalTemplate.vue'))),
)

const hasFooter = computed(
  () => (props.actions && props.actions.length) || (slots.footer && props.footer) || props.hasClose,
)

watch(
  () => props.actions,
  (data) => {
    if (Array.isArray(data)) {
      data.forEach((action, id) => {
        loadingObject.value[id] = false
      })
    }
  },
  {
    immediate: true,
  },
)

function onOk(event) {
  props.resolve(event)
  closeModal()
}

async function modalAction(index, data) {
  if (!data.action) {
    throw new Error('Action is not exist')
  }
  if (data.loading) {
    loadingObject.value[index] = true
    try {
      await data.action()
    } catch {
      throw new Error('You must use Promise action with loading props === true')
    } finally {
      closeModal()
    }
  } else {
    if (data.action !== 'close') {
      data.action()
    }
    closeModal()
  }
}

function backToRegistrationModal() {
  register()
}

function onCancel(value) {
  if (props.reject) {
    props.reject(value)
  }
}

function closeModal(value) {
  document.documentElement.style.scrollBehavior = 'auto'
  setTimeout(() => {
    document.documentElement.style.scrollBehavior = null
  }, 100)
  if (useSiteInfoStore().isWebView) {
    postMessageFromWebviewToApp({ type: 'modal', value: 'closed' })
  }
  loadingObject.value = {}
  onCancel(value)
  emit('update:model-value')
  if (route.query.modal) {
    router.replace({ 'query.modal': null })
  }
  useModalsStore().closeCustomModal()
}
</script>

<style lang="scss">
.custom-modal-vfm {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  z-index: 1052 !important;

  .vfm__overlay {
    background-color: rgba(#121212, 0.6);
  }

  &[horizontal-align='right'] {
    justify-content: flex-end;

    .vfm__content {
      justify-content: flex-end;
    }
  }

  &[horizontal-align='left'] {
    justify-content: flex-start;

    .vfm__content {
      justify-content: flex-start;
    }
  }

  @media (min-height: 1024px) {
    padding: calc(2% + 25px) 0;
  }
}

.custom-modal-vfm--bottom {
  align-items: flex-end;

  &.custom-modal-vfm--seamless {
    top: auto;
  }

  @include tab {
    align-items: center;
  }
}

.custom-modal--maximized {
  min-height: 100vh;

  .custom-modal {
    width: 100vw !important;
  }

  @supports (height: 100dvh) {
    min-height: 100dvh;
  }
}

.touch-device {
  pointer-events: all !important;
}

.custom-modal {
  width: 100%;
  background-color: var(--color-background-page-lvl-2);
  color: var(--color-text-primary);
  border-radius: var(--radius-3);
  overflow: visible !important;
  display: flex;
  flex-direction: column;
  position: relative;
  max-height: 100% !important;
  max-width: none !important;

  @media (min-width: 768px) {
    min-width: 450px;
    max-width: 80vw !important;
  }

  &:not(.custom-modal--maximized) {
    @media (max-width: 768px) {
      border-radius: var(--radius-3) var(--radius-3) 0 0;
    }
  }

  &__header-wrapper {
    display: flex;
    align-items: center;
    gap: 8px;
    min-height: 64px;
    color: var(--color-text-primary);
    border-radius: var(--radius-3) var(--radius-3) 0 0;
    flex: none;

    @include mobile {
      border-radius: 0;
    }

    &:not(:empty) {
      background-color: var(--color-background-page-lvl-3);
      height: auto;
      padding: 16px 56px 16px 16px;

      @media (min-width: 768px) {
        padding: 24px 56px 24px 24px;
      }

      @media (min-width: 1024px) {
        padding-right: 24px;
      }
    }
  }

  &__header {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    flex: 1 1;
  }

  &__back-btn {
    display: flex;
    align-items: center;
    column-gap: 4px;
    width: 75px;
    margin: 20px 0 -17px;
    color: var(--color-text-secondary);
    font-size: 16px;
    font-weight: 700;
    line-height: 20px;
    cursor: pointer;

    @media (max-width: 767px) {
      margin: 20px 0 -12px;
    }
  }

  &__close-btn {
    position: absolute;
    top: 20px;
    right: 16px;
    z-index: 1;
    width: 24px;
    height: 24px;
    margin: auto;
    flex-shrink: 0;
    font-size: 24px;
    display: flex;
    align-items: center;
    cursor: pointer;
    color: var(--color-icon-tertiary);
    transition: 0.3s ease;

    .olimpbet & {
      color: var(--color-icon-secondary);
    }

    &:active {
      color: var(--color-icon-secondary);
    }

    @media (min-width: 600px) {
      &:hover {
        color: var(--color-icon-secondary);
      }
    }

    @media (min-width: 768px) {
      top: 22px;
      right: 20px;
    }
  }

  &__body {
    font-size: 16px;
    padding: 16px;
    overflow: auto;
    flex: 1 1;

    p {
      margin-bottom: 1em;
    }

    @media (min-width: 768px) {
      padding: 24px;
    }

    &--ios {
      padding-bottom: 56px;
    }
  }

  &__actions {
    display: flex;
    flex-direction: column;
    gap: 16px;
    margin-top: 24px;

    .custom-modal--big-size & {
      @media (min-width: 768px) {
        flex-direction: row;
        justify-content: flex-end;

        & > button {
          flex: 1 1;
        }
      }
    }
  }

  &__footer {
    flex: none;
    padding: 16px 16px 24px 16px;
    border-top: var(--border-width-xs) solid var(--color-border-primary);
    display: flex;
    flex-direction: column;
    gap: 8px;

    @media (min-width: 600px) {
      flex-direction: row-reverse;
      flex-wrap: wrap;
      justify-content: flex-start;
    }

    @media (min-width: 768px) {
      padding: 24px;
    }
  }
}
</style>
